import create from 'zustand'
import merge from 'deepmerge'

import { langSet } from './../../../constants/lang'

import {
  DEFAULT_VIEWPORT,
  DEFAULT_VIEW,
  DEFAULT_ACTIVE_SHAPE,
  DEFAULT_ACTIVE_YEAR,
  DEFAULT_LOAD_YEARS,
  DEFAULT_ACTIVE_POINTS,
  DEFAULT_METRIC,
  DEFAULT_NORM,
  OPTIONS_NORM,
  DEFAULT_GEO,
  DEFAULT_ACTIVE_REDLINING,
  DEFAULT_RATING_TYPE,
  DEFAULT_DATA_VERSION,
} from './../../../constants/map'

const useStore = create((set, get) => ({
  // Set any store values by passing in an object of values to merge.
  setStoreValues: obj => {
    set({ ...obj })
  },
  // Store browser info.
  browser: null,
  // Is the browser supported?
  unsupportedBrowser: false,
  // Track loading of remote data files.
  allDataLoaded: false,
  // Percent loaded for remote data files.
  dataLoadedPercent: 0,
  // Error flag for loading failure.
  dataLoaderFailed: false,
  // JSON files loaded from remote.
  remoteJson: {},
  setRemoteJson: json =>
    set(state => {
      const newRemoteJson = { ...state.remoteJson }

      for (const key of Object.keys(json)) {
        // Overwrite large arrays first before deleting (hint to garbage collector)
        newRemoteJson[key] = null
        delete newRemoteJson[key]
      }

      // Merge in new data
      return {
        remoteJson: {
          ...newRemoteJson,
          ...json,
        },
      }
    }),
  // Language packs handling.
  // Active language
  activeLang: `en_US`,
  // Counter for lang pack updates.
  langUpdates: 0,
  incrementLangUpdates: () => {
    set(state => ({
      langUpdates: state.langUpdates + 1,
    }))
  },
  // Languages store.
  langs: {
    en_US: langSet.en_US,
  },
  // Get a language.
  getLang: loc => {
    return get().langs[loc]
  },
  // Merge existing language strings with any new ones.
  setLang: obj => {
    // console.log('setLang')
    set({ langs: merge(get().langs, obj) })
  },
  // Routing.
  activeView: DEFAULT_VIEW,
  activeGeoType: DEFAULT_GEO,
  // ID of the feature that's been clicked, used by MapBox.
  // TODO: merge with slideoutFeature?
  activeShape: DEFAULT_ACTIVE_SHAPE,
  activeYear: DEFAULT_ACTIVE_YEAR,
  // demographic dot density layers
  activeRedliningTypes: DEFAULT_ACTIVE_REDLINING,
  redliningActive: false,

  activePointLayers: DEFAULT_ACTIVE_POINTS,
  showDensityOverlays: false,
  toggleDensityOverlays: () => {
    const showDensityOverlays = !get().showDensityOverlays
    const layers = get().activePointLayers
    // if activating overlays and all layers are off, turn them all on
    const activePointLayers =
      showDensityOverlays && !layers.length
        ? DEFAULT_ACTIVE_POINTS
        : layers
    set({
      showDensityOverlays,
      activePointLayers,
    })
  },
  // data version is fixed, not customizable via route
  dataVersion: DEFAULT_DATA_VERSION,
  // Which years of tilesets to load.
  loadYears: DEFAULT_LOAD_YEARS,
  activeRatingType: DEFAULT_RATING_TYPE,
  activeMetric: DEFAULT_METRIC,
  // Norming info
  optionsNorm: OPTIONS_NORM,
  activeNorm: DEFAULT_NORM,
  // Flag to trigger download of data dependent upon hash.
  initialStateSetFromHash: false,
  // Map sources, stored so we don't have to construct it over and over.
  mapSources: false,
  // Stores the ID of the feature displayed in the slideout panel.
  slideoutFeatureId: 0,
  // Stores the feature of the feature displayed in the slideout panel.
  slideoutFeature: null,
  // Hovered feature.
  hoveredFeatureId: 0,
  // Highlight a feature after flyTo
  flyingToLocation: false,
  // Is the user currently interacting with the map?
  mapInteractionState: false,
  // Array of previously hovered features.
  hoveredFeatureIdArr: [],
  pushHoveredFeatureId: featureId => {
    set(state => ({
      hoveredFeatureIdArr: [
        ...state.hoveredFeatureIdArr,
        featureId,
      ],
    }))
  },
  // Hovered feature.
  hoveredFeature: null,
  activeRegion: null,
  POIModeActive: false,
  activePOI: '2',
  loadFromPOIUrl: false,
  // always keep the activeView in sync with activePOI (for hash)
  setActivePOI: activePOI =>
    set({ activePOI, activeView: 'wkkf' + activePOI }),
  // Mouse XY.
  mouseXY: [0, 0],
  // Mouse coords.
  coords: [0, 0],
  // Map dimensions, [width, height]
  mapSize: [],
  // Window innerHeight
  windowInnerHeight: 0,
  // Settings pertaining to viewport state.
  viewport: { ...DEFAULT_VIEWPORT },
  resetViewport: { ...DEFAULT_VIEWPORT },
  setViewport: viewport =>
    set(state => ({
      viewport: { ...state.viewport, ...viewport },
    })),
  flyToBounds: null,
  flyToFeature: null,
  flyToLatLon: null,
  flyToReset: null,
  flyToState: null,
  slideoutPanel: {
    active: false,
    panel: 'control', // location, chart, control, faq
  },
  variabilityChartMetric: null,
  // Popover for share button in control panel
  showSharePopover: false,
  legendPanel: {
    active: false,
    open: false, // chart open state TODO move to slideout
    glow: 0,
  },
  // mobile controls. merge with desktop controls (slideoutPanel) state?
  legendControlActive: false,
  expandedFaqs: {},
  notification: null,
  activeUserHint: {},
  // Have strings from cms been loaded.
  cmsStringsLoaded: false,
  shareLinkModal: false,
  // Whether or not to display slideout menu.
  showMenu: false,
  toggleShowMenu: () => {
    set(state => ({ showMenu: !state.showMenu }))
  },
  shareHash: null,
  breakpoint: null,
  browserWidth: null,
  showIntroModal: false,
  showPanelModal: false,
  enableTour: true, // Set this true to show the launch tour button in intro modal.
  showMapModal: false,
  // Display tooltip, boolean
  displayPopup: true,
  showZipBoundaries: false,
  // Position of tooltips in control panel, changes with breakpoint
  buttonTooltipPosition: 'auto',
  showMobileLegend: false,
  interactionsMobile: false,
  runTour: false,
  tourStepIndex: 0,
  // Set up for tour to run.
  setUpTour: () => {
    set(state => ({
      // Return view to map.
      activeView: 'explorer',
      // Reset metric.
      activeMetric: DEFAULT_METRIC,
      // Reset quintiles.
      activeQuintiles: [1, 1, 1, 1, 1],
      // Close the panel.
      slideoutPanel: {
        active: false,
        panel: '',
      },
      // Active tab in slideout panel.
      activeFilterTab: state.defaultFilterTab,
      // Close modal if displayed.
      showPanelModal: false,
      // Return tour to 0.
      tourStepIndex: 0,
      // Run the tour.
      runTour: true,
    }))
  },
  isTouchScreen: false,
  // Do not track events before map is loaded, as these
  // are state settings based on hash and not user interactions.
  doTrackEvents: false,
  // Counters for events that don't have clear state indicators.
  eventShareTwitter: 0,
  eventShareFacebook: 0,
  eventShareEmail: 0,
  eventShareLink: 0,
  eventShareEmbed: 0,
  eventMapReset: 0,
  eventMapCapture: 0,
  eventSchoolSearch: 0,
  eventSchoolPage: 0,
  eventLaunchTour: 0,
  eventCloseTour: 0,
  eventCloseTourStep: null,
  incrementLaunchTour: () => {
    set(state => ({
      eventLaunchTour: state.eventLaunchTour + 1,
    }))
  },
  incrementCloseTour: () => {
    set(state => ({
      eventCloseTour: state.eventCloseTour + 1,
      eventCloseTourStep: state.tourStepIndex,
    }))
  },
  incrementEvent: event => {
    set(state => ({
      [event]: state[event] + 1,
    }))
  },
  // Not counters.
  eventError: 0,
}))
const api = useStore

export default useStore
